import { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageInit } from '../hooks/usePageInit';
import { useAuthGuard } from '../hooks/useAuthGuard';
import './LandingPage.css';
import { collection, getDocs, limit as fbLimit, orderBy as fbOrderBy, query as fbQuery } from 'firebase/firestore';
import { db } from '../config/firebase';
import BlockRenderer, { ContentBlock } from '../components/BlockRenderer';
import { initAnalytics, trackEvent, updateAnalyticsConsent } from '../utils/analytics';
import AuthRequiredLink from '../components/AuthRequiredLink';

type LpTarget = 'school' | 'student' | 'both' | 'lp';

type LpFAQ = {
  id: string;
  question: string;
  answer?: string;
  blocks?: ContentBlock[];
  target?: LpTarget;
  targets?: LpTarget[];
  status?: 'visible' | 'hidden' | 'scheduled';
  publishedAt?: any;
  order?: number;
};

type LpNotice = {
  id: string;
  title: string;
  content?: string;
  blocks?: ContentBlock[];
  target?: LpTarget;
  targets?: LpTarget[];
  publishedAt?: any;
  expiresAt?: any;
};

// 実機スクリーンショットの差し込み先（用意でき次第ここに配置）
// 推奨: スマホ縦キャプチャ（例 iPhone 1170×2532px）/ PNG
const IMG = {
  schedule: '/assets/images/app/schedule.png',
  reschedule: '/assets/images/app/reschedule.png',
  tuition: '/assets/images/app/tuition.png',
};
const APP_STORE_URL = 'https://apps.apple.com/jp/app/lessonmate/id6751098543';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=inc.ludicrous.lessonmate';

function toMillis(v: any): number | null {
  try {
    if (!v) return null;
    if (typeof v?.toMillis === 'function') return v.toMillis();
    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    const d = new Date(v as any);
    const ms = d.getTime();
    return Number.isNaN(ms) ? null : ms;
  } catch {
    return null;
  }
}

function formatDate(v: any): string {
  const ms = toMillis(v);
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default function LandingPage() {
  usePageInit();
  useAuthGuard();

  const [faqs, setFaqs] = useState<LpFAQ[]>([]);
  const [notices, setNotices] = useState<LpNotice[]>([]);
  const [cookieConsent, setCookieConsent] = useState<'accepted' | 'rejected' | null>(null);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);

  // Firestore から LP 向け FAQ / お知らせ を取得
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const nowMs = Date.now();
        const faqSnap = await getDocs(
          fbQuery(collection(db, 'faqs'), fbOrderBy('order', 'asc'), fbLimit(50))
        );
        const faqItems: LpFAQ[] = faqSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        const filteredFaqs = faqItems.filter((f: any) => {
          const targets: string[] = Array.isArray(f?.targets) ? f.targets.map((x: any) => String(x)) : [];
          const isLp = targets.includes('lp') || String(f?.target || '') === 'lp';
          if (!isLp) return false;
          const status = String(f?.status || 'visible');
          if (status === 'hidden') return false;
          if (status === 'scheduled') {
            const pubMs = toMillis(f?.publishedAt);
            if (!pubMs || pubMs > nowMs) return false;
          }
          return true;
        });

        const noticeSnap = await getDocs(
          fbQuery(collection(db, 'notices'), fbOrderBy('publishedAt', 'desc'), fbLimit(30))
        );
        const noticeItems: LpNotice[] = noticeSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        const filteredNotices = noticeItems.filter((n: any) => {
          const targets: string[] = Array.isArray(n?.targets) ? n.targets.map((x: any) => String(x)) : [];
          const isLp = targets.includes('lp') || String(n?.target || '') === 'lp';
          if (!isLp) return false;
          const pubMs = toMillis(n?.publishedAt);
          const expMs = toMillis(n?.expiresAt);
          if (pubMs && pubMs > nowMs) return false;
          if (expMs && expMs <= nowMs) return false;
          return true;
        });

        if (!alive) return;
        setFaqs(filteredFaqs);
        setNotices(filteredNotices);
      } catch (e) {
        console.warn('Failed to load LP content from Firestore:', e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Cookie同意
  useEffect(() => {
    try {
      const v = localStorage.getItem('lp_cookie_consent');
      setCookieConsent(v === 'accepted' || v === 'rejected' ? v : null);
    } catch {
      setCookieConsent(null);
    }
  }, []);

  // Preloader / loading overlay を確実に隠す
  useEffect(() => {
    const preloader = document.getElementById('preloader');
    if (preloader) preloader.style.display = 'none';
    const loading = document.querySelector('.loading');
    if (loading) (loading as HTMLElement).style.display = 'none';
  }, []);

  const faqItems = useMemo<LpFAQ[]>(() => {
    if (faqs.length > 0) return faqs;
    return [
      { id: 'f1', question: '現金のまま使い始められますか？', answer: 'はい。まずは現金集金のままレッスン管理・スケジュール・お知らせ機能をご利用いただけます。キャッシュレス集金は後から有効化できます。' },
      { id: 'f2', question: 'クレジットカード決済は生徒側が操作しますか？', answer: 'はい。生徒（保護者）が登録したカードで各教室の月謝を支払い、教室側は入金状況を一覧で把握できます。' },
      { id: 'f3', question: '振替の回数や締切は設定できますか？', answer: '「何ヶ月で何回まで」「何日前まで」「再振替の可否」などを教室ごとに設定できます。' },
      { id: 'f4', question: '導入に必要な準備は？', answer: 'アプリのダウンロードと教室情報の登録から。生徒はQRコードで簡単に招待できます。' },
    ];
  }, [faqs]);

  const lpNoticesTop3 = useMemo(() => (notices || []).slice(0, 3), [notices]);

  const onCta = (placement: string) => {
    try { trackEvent('cta_click', { placement }); } catch {}
  };

  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var" id="main-wrap">
        <div id="home"></div>
        <div className="main-wrap">
          <Header />

          <main className="container-wrap">
            <div className="lp2">

              {/* ===== HERO ===== */}
              <section id="banner" className="lp2-hero">
                <div className="lp2-inner lp2-hero-grid">
                  <div className="lp2-hero-copy">
                    <span className="lp2-badge">レッスンの振替・欠席連絡に対応</span>
                    <h1 className="lp2-h1">
                      振替も、欠席連絡も、月謝も。<br />
                      <span className="lp2-accent">教室の管理に便利な機能を、ひとつのアプリで。</span>
                    </h1>
                    <p className="lp2-lead">
                      カレンダーでレッスン管理。ワンタップで振替申請。
                      月謝はキャッシュレスで自動集金。
                    </p>
                    <div className="lp2-cta-row">
                      <div className="lp2-stores">
                        <a href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => trackEvent('store_click', { store: 'app_store', placement: 'hero' })}>
                          <img src="/assets/images/mobile/app-store.png" alt="App Store" />
                        </a>
                        <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" onClick={() => trackEvent('store_click', { store: 'play_store', placement: 'hero' })}>
                          <img src="/assets/images/mobile/play-store.png" alt="Google Play" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="lp2-hero-visual">
                    <div className="lp2-shot-frame">
                      <img src={IMG.schedule} alt="LessonMate スケジュール画面" className="lp2-shot" loading="eager" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    </div>
                  </div>
                </div>
                <div className="lp2-hero-bg" aria-hidden="true"></div>
              </section>

              {/* ===== PROBLEM ===== */}
              <section className="lp2-section lp2-problem">
                <div className="lp2-inner">
                  <h2 className="lp2-h2">こんなこと、ありませんか？</h2>
                  <div className="lp2-cards3">
                    <div className="lp2-card">
                      <div className="lp2-emoji">🔁</div>
                      <h3>振替の調整が毎回大変</h3>
                      <p>メッセージのやり取り、候補日の提示など双方に手間がかかる</p>
                    </div>
                    <div className="lp2-card">
                      <div className="lp2-emoji">💴</div>
                      <h3>集金・記帳に毎月手間</h3>
                      <p>先生も生徒も、キャッシュレスで簡単。アプリで一目瞭然。</p>
                    </div>
                    <div className="lp2-card">
                      <div className="lp2-emoji">🧒</div>
                      <h3>現金を持たせるのが心配</h3>
                      <p>毎月の月謝を子どもに持たせるのは、紛失や渡し忘れも気がかり。</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== FEATURE 1: SCHEDULE / RESCHEDULE ===== */}
              <section id="feature-title" className="lp2-section lp2-feature">
                <div className="lp2-inner lp2-feature-grid">
                  <div className="lp2-feature-visual">
                    <div className="lp2-shot-frame">
                      <img src={IMG.reschedule} alt="振替申請の画面" className="lp2-shot" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    </div>
                  </div>
                  <div className="lp2-feature-copy">
                    <span className="lp2-eyebrow">SCHEDULE &amp; 振替</span>
                    <h2 className="lp2-h2">振替の連絡、もう手作業じゃない。</h2>
                    <ul className="lp2-checklist">
                      <li>年・月・週・日でレッスンを一覧。教室ロゴ付きで一目で分かる</li>
                      <li>予定をタップして「振替申請／欠席連絡」を選ぶだけ</li>
                      <li>候補日をタップで送信。確定すると自動で予定に反映＆通知</li>
                      <li>回数上限・申請締切・再振替の可否まで、教室の運用に合わせて設定可能</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* ===== FEATURE 2: CASHLESS PAYMENT (revenue goal) ===== */}
              <section className="lp2-section lp2-feature lp2-feature-alt">
                <div className="lp2-inner lp2-feature-grid lp2-reverse">
                  <div className="lp2-feature-visual">
                    <div className="lp2-shot-frame">
                      <img src={IMG.tuition} alt="月謝・キャッシュレス集金の画面" className="lp2-shot" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    </div>
                  </div>
                  <div className="lp2-feature-copy">
                    <span className="lp2-eyebrow">月謝のキャッシュレス集金</span>
                    <h2 className="lp2-h2">集金の手間も、払い忘れも、ゼロへ。</h2>
                    <ul className="lp2-checklist">
                      <li>毎月の月謝を自動で集金。記帳や声かけの手間が消える</li>
                      <li>毎月きちんと入金され、収入が安定する</li>
                      <li>現金とカードの併用OK。まずは希望する保護者だけでも始められる</li>
                    </ul>
                    <p className="lp2-note">
                      集金にかける時間 × 時給 ＋ 払い忘れによるロス ＞ 決済手数料。
                      現金運用の“隠れコスト”を、キャッシュレスでまるごと削減します。
                    </p>
                  </div>
                </div>
              </section>

              {/* ===== OTHER FEATURES ===== */}
              <section id="showcase-title" className="lp2-section lp2-others">
                <div className="lp2-inner">
                  <h2 className="lp2-h2">教室運営に必要な機能を、まるごと。</h2>
                  <div className="lp2-grid-icons">
                    {[
                      ['🗓️', 'スケジュール・振替', '振替申請・欠席連絡まで完結'],
                      ['💳', 'キャッシュレス集金', '月謝を自動で集金・管理'],
                      ['📨', 'お知らせ配信', '教室から保護者へ一斉連絡'],
                      ['💬', 'チャット', '保護者との個別連絡もアプリで'],
                      ['🔔', 'お支払い管理', '入金状況を見える化し、やさしくフォロー'],
                      ['👨‍👩‍👧', '複数受講者・複数教室', '兄弟・掛け持ちもまとめて管理'],
                    ].map(([emoji, title, desc]) => (
                      <div className="lp2-icon-card" key={title as string}>
                        <div className="lp2-emoji">{emoji}</div>
                        <h3>{title}</h3>
                        <p>{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ===== STEPS ===== */}
              <section className="lp2-section lp2-steps">
                <div className="lp2-inner">
                  <h2 className="lp2-h2">導入はかんたん3ステップ</h2>
                  <div className="lp2-cards3">
                    <div className="lp2-step">
                      <span className="lp2-step-no">1</span>
                      <h3>アプリDL・教室登録</h3>
                      <p>無料で始められます。教室情報を登録するだけ。</p>
                    </div>
                    <div className="lp2-step">
                      <span className="lp2-step-no">2</span>
                      <h3>生徒をQRで招待</h3>
                      <p>QRコードを共有するだけで保護者が登録できます。</p>
                    </div>
                    <div className="lp2-step">
                      <span className="lp2-step-no">3</span>
                      <h3>月謝・スケジュール設定</h3>
                      <p>コース・金額・レッスン日を設定して運用開始。</p>
                    </div>
                  </div>
                  <p className="lp2-steps-note">まずは現金のままでOK。慣れたらキャッシュレス集金へ。</p>
                </div>
              </section>

              {/* ===== PRICING ===== */}
              <section className="lp2-section lp2-pricing">
                <div className="lp2-inner lp2-pricing-box">
                  <h2 className="lp2-h2">料金について</h2>
                  <div className="lp2-pricing-grid">
                    <div className="lp2-price-card">
                      <div className="lp2-price-label">アプリ利用</div>
                      <div className="lp2-price-value">無料</div>
                      <p>レッスン管理・スケジュール・お知らせ・チャットすべて利用できます。</p>
                    </div>
                    <div className="lp2-price-card">
                      <div className="lp2-price-label">キャッシュレス集金</div>
                      <div className="lp2-price-value">決済時のみ手数料</div>
                      <p>カード決済を使った月謝集金のときだけ手数料が発生。現金集金は無料です。</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== TESTIMONIALS（一旦非表示。実教室の声が出せるようになったら復活） =====
              <section id="testimonials" className="lp2-section lp2-voices">
                <div className="lp2-inner">
                  <h2 className="lp2-h2">導入いただいた教室の声</h2>
                  <div className="lp2-cards3">
                    <figure className="lp2-quote">
                      <blockquote>「振替のやりとりが激減。生徒側もアプリで完結するので楽になりました。」</blockquote>
                      <figcaption>ピアノ教室・主宰</figcaption>
                    </figure>
                    <figure className="lp2-quote">
                      <blockquote>「毎月の集金と記帳が無くなって、レッスンに集中できるように。」</blockquote>
                      <figcaption>個人塾・講師</figcaption>
                    </figure>
                    <figure className="lp2-quote">
                      <blockquote>「お知らせもチャットもまとまり、保護者対応がスムーズになりました。」</blockquote>
                      <figcaption>英会話教室・運営</figcaption>
                    </figure>
                  </div>
                </div>
              </section>
              */}

              {/* ===== NEWS ===== */}
              {lpNoticesTop3.length > 0 && (
                <section className="lp2-section lp2-news">
                  <div className="lp2-inner">
                    <h2 className="lp2-h2">お知らせ</h2>
                    <ul className="lp2-news-list">
                      {lpNoticesTop3.map((n) => (
                        <li key={n.id}>
                          <AuthRequiredLink to={`/notices/${n.id}`} className="lp2-news-item">
                            <span className="lp2-news-date">{formatDate(n.publishedAt)}</span>
                            <span className="lp2-news-title">{n.title}</span>
                            <span className="lp2-news-arrow">›</span>
                          </AuthRequiredLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              )}

              {/* ===== FAQ ===== */}
              <section className="lp2-section lp2-faq">
                <div className="lp2-inner lp2-faq-inner">
                  <h2 className="lp2-h2">よくある質問</h2>
                  <div className="lp2-accordion">
                    {faqItems.slice(0, 6).map((f) => {
                      const open = openFaqId === f.id;
                      return (
                        <div className={`lp2-acc-item ${open ? 'open' : ''}`} key={f.id}>
                          <button
                            type="button"
                            className="lp2-acc-q"
                            onClick={() => setOpenFaqId(open ? null : f.id)}
                            aria-expanded={open}
                          >
                            <span>{f.question}</span>
                            <span className="lp2-acc-icon">{open ? '−' : '+'}</span>
                          </button>
                          {open && (
                            <div className="lp2-acc-a">
                              {Array.isArray(f.blocks) && f.blocks.length > 0 ? (
                                <BlockRenderer blocks={f.blocks} />
                              ) : (
                                <p>{f.answer}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="lp2-faq-more">
                    <a href="/faq" className="lp2-link">よくある質問をすべて見る ›</a>
                  </div>
                </div>
              </section>

              {/* ===== FINAL CTA ===== */}
              <section className="lp2-section lp2-finalcta">
                <div className="lp2-inner lp2-finalcta-box">
                  <h2 className="lp2-h2 lp2-white">まずは相談から、はじめましょう。</h2>
                  <p className="lp2-finalcta-sub">導入のご相談・資料請求はお気軽にどうぞ。</p>
                  <AuthRequiredLink to="/contact" className="lp2-btn lp2-btn-white" onClick={() => onCta('final')}>
                    お問い合わせ・資料請求
                  </AuthRequiredLink>
                </div>
              </section>
            </div>
          </main>

          <Footer withDeco={true} />
        </div>
      </div>

      {/* Cookie 同意 */}
      {cookieConsent === null && (
        <div className="lp2-cookie">
          <p>
            当サイトでは利用状況の分析のためCookie等を使用する場合があります。詳細は
            <a href="/privacy-policy">プライバシーポリシー</a>をご確認ください。
          </p>
          <div className="lp2-cookie-actions">
            <button
              type="button"
              className="lp2-btn lp2-btn-primary lp2-btn-sm"
              onClick={() => {
                try { localStorage.setItem('lp_cookie_consent', 'accepted'); } catch {}
                setCookieConsent('accepted');
                initAnalytics();
                updateAnalyticsConsent(true);
              }}
            >
              同意する
            </button>
            <button
              type="button"
              className="lp2-btn lp2-btn-ghost lp2-btn-sm"
              onClick={() => {
                try { localStorage.setItem('lp_cookie_consent', 'rejected'); } catch {}
                setCookieConsent('rejected');
              }}
            >
              拒否する
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
